import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Button, Droplet, Splash } from "@dp/ui";
import { getDictionary } from "@dp/i18n";
import { loadConfig, ConfigProvider, type AppConfig } from "./config.js";
import { initClient } from "./api/client.js";
import { LocaleProvider } from "./i18n/LocaleProvider.js";
import { AuthProvider, useAuth } from "./auth/AuthProvider.js";
import { PublicLayout, ProtectedLayout } from "./shell/layouts.js";
import { AppErrorBoundary } from "./components/AppErrorBoundary.js";
import { initTelemetry } from "./telemetry.js";

// Route-level code splitting: each screen is its own chunk so the
// initial download stays lean. Named exports are adapted to lazy's default shape.
const Welcome = lazy(() => import("./routes/Welcome.js").then((m) => ({ default: m.Welcome })));
const Login = lazy(() => import("./routes/Login.js").then((m) => ({ default: m.Login })));
const Register = lazy(() => import("./routes/Register.js").then((m) => ({ default: m.Register })));
const Forgot = lazy(() => import("./routes/Forgot.js").then((m) => ({ default: m.Forgot })));
const Home = lazy(() => import("./routes/Home.js").then((m) => ({ default: m.Home })));
const WalletDetail = lazy(() => import("./routes/WalletDetail.js").then((m) => ({ default: m.WalletDetail })));
const Activity = lazy(() => import("./routes/Activity.js").then((m) => ({ default: m.Activity })));
const Recharge = lazy(() => import("./routes/Recharge.js").then((m) => ({ default: m.Recharge })));
const RechargeResult = lazy(() => import("./routes/RechargeResult.js").then((m) => ({ default: m.RechargeResult })));
const RechargeHistory = lazy(() => import("./routes/RechargeHistory.js").then((m) => ({ default: m.RechargeHistory })));
const Cards = lazy(() => import("./routes/Cards.js").then((m) => ({ default: m.Cards })));
const CardActivate = lazy(() => import("./routes/CardActivate.js").then((m) => ({ default: m.CardActivate })));
const CardDetail = lazy(() => import("./routes/CardDetail.js").then((m) => ({ default: m.CardDetail })));
const Profile = lazy(() => import("./routes/Profile.js").then((m) => ({ default: m.Profile })));
const Help = lazy(() => import("./routes/Help.js").then((m) => ({ default: m.Help })));

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: true } },
});

/** Holds the router behind the silent-refresh boot so we don't flash the
 *  welcome screen before deciding the customer is actually logged in. */
function BootGate({ children }: { children: React.ReactNode }) {
  const { phase } = useAuth();
  if (phase === "booting") return <Splash />;
  return <>{children}</>;
}

export function App() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    loadConfig().then(
      (c) => {
        initClient(c);
        initTelemetry(c);
        setConfig(c);
      },
      () => setFailed(true),
    );
  }, []);

  if (failed) {
    const t = getDictionary("en");
    return (
      <div className="splash">
        <Droplet size={48} />
        <p className="muted">{t.errors.network}</p>
        <Button variant="secondary" onClick={() => location.reload()}>
          {t.common.retry}
        </Button>
      </div>
    );
  }

  if (!config) return <Splash />;

  return (
    <ConfigProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <LocaleProvider>
          <BrowserRouter>
            <AuthProvider>
              <BootGate>
                <AppErrorBoundary>
                <Suspense fallback={<Splash />}>
                <Routes>
                  <Route element={<PublicLayout />}>
                    <Route path="/welcome" element={<Welcome />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot" element={<Forgot />} />
                  </Route>
                  {/* Recharge return pages are auth-optional-first: they cold-boot
                      after the gateway redirect destroys the memory token, so they
                      live OUTSIDE the protected shell (no tab bar) and handle their
                      own auth state via polling. */}
                  <Route path="/recharge/result" element={<RechargeResult />} />
                  <Route path="/recharge/success" element={<RechargeResult />} />
                  <Route path="/recharge/fail" element={<RechargeResult />} />
                  <Route path="/recharge/cancel" element={<RechargeResult />} />
                  <Route element={<ProtectedLayout />}>
                    <Route path="/home" element={<Home />} />
                    <Route path="/wallets/:id" element={<WalletDetail />} />
                    <Route path="/activity" element={<Activity />} />
                    <Route path="/recharge" element={<Recharge />} />
                    <Route path="/recharges" element={<RechargeHistory />} />
                    <Route path="/cards" element={<Cards />} />
                    <Route path="/cards/activate" element={<CardActivate />} />
                    <Route path="/cards/:cardId" element={<CardDetail />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/help" element={<Help />} />
                  </Route>
                  <Route path="/" element={<Navigate to="/home" replace />} />
                  <Route path="*" element={<Navigate to="/home" replace />} />
                </Routes>
                </Suspense>
                </AppErrorBoundary>
              </BootGate>
            </AuthProvider>
          </BrowserRouter>
        </LocaleProvider>
      </QueryClientProvider>
    </ConfigProvider>
  );
}
